import React, { useState, useEffect, useCallback } from "react";

/**
 * SelfieModal – a global image-preview popup.
 *
 * Listen for the custom event "show-selfie-modal" dispatched by Utils.viewImage().
 * Renders a full-screen backdrop + centred image card with zoom, close button
 * and keyboard / click-outside dismiss.
 */
const SelfieModal = () => {
  const [imgSrc, setImgSrc] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Open when the global event fires
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.src) {
        setImgSrc(e.detail.src);
        setZoomed(false);
        setVisible(true);
      }
    };
    window.addEventListener("show-selfie-modal", handler);
    return () => window.removeEventListener("show-selfie-modal", handler);
  }, []);

  // Keyboard ESC closes the modal
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && visible) close();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const close = () => {
    setVisible(false);
    setImgSrc(null);
    setZoomed(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* ── Backdrop ─────────────────────────────────────────────── */}
      <div
        onClick={close}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          animation: "selfie-fade-in 0.2s ease",
        }}
      />

      {/* ── Modal card ───────────────────────────────────────────── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: "auto",
            background: "linear-gradient(145deg,#1e1e2e,#16213e)",
            borderRadius: 16,
            boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07)",
            padding: 16,
            maxWidth: "min(520px, 94vw)",
            width: "100%",
            animation: "selfie-pop-in 0.25s cubic-bezier(0.34,1.56,0.64,1)",
          }}
        >
          {/* Header row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i
                className="bi bi-person-bounding-box"
                style={{ fontSize: 20, color: "#7c83fd" }}
              />
              <span
                style={{
                  color: "#e2e2ef",
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: 0.3,
                }}
              >
                Selfie Preview
              </span>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {/* Zoom toggle */}
              <button
                title={zoomed ? "Zoom out" : "Zoom in"}
                onClick={() => setZoomed((z) => !z)}
                style={iconBtnStyle}
              >
                <i
                  className={`bi ${zoomed ? "bi-zoom-out" : "bi-zoom-in"}`}
                  style={{ fontSize: 16, color: "#a0a4e0" }}
                />
              </button>

              {/* Download */}
              <a
                href={imgSrc}
                download="selfie.jpg"
                title="Download"
                style={{ ...iconBtnStyle, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <i className="bi bi-download" style={{ fontSize: 16, color: "#a0a4e0" }} />
              </a>

              {/* Close */}
              <button
                title="Close  (Esc)"
                onClick={close}
                style={iconBtnStyle}
              >
                <i className="bi bi-x-lg" style={{ fontSize: 16, color: "#a0a4e0" }} />
              </button>
            </div>
          </div>

          {/* Image */}
          <div
            style={{
              borderRadius: 10,
              overflow: "hidden",
              background: "#0d0d1a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              cursor: zoomed ? "zoom-out" : "zoom-in",
              transition: "background 0.2s",
            }}
            onClick={() => setZoomed((z) => !z)}
          >
            <img
              src={imgSrc}
              alt="Selfie"
              style={{
                maxWidth: "100%",
                maxHeight: zoomed ? "75vh" : 380,
                objectFit: "contain",
                borderRadius: 8,
                transition: "max-height 0.3s ease, transform 0.3s ease",
                transform: zoomed ? "scale(1.05)" : "scale(1)",
                display: "block",
              }}
            />
          </div>

          {/* Footer hint */}
          <p
            style={{
              marginTop: 10,
              marginBottom: 0,
              textAlign: "center",
              fontSize: 11,
              color: "rgba(255,255,255,0.3)",
              letterSpacing: 0.2,
            }}
          >
            Click image to zoom · Press&nbsp;<kbd style={kbdStyle}>Esc</kbd>&nbsp;or click outside to close
          </p>
        </div>
      </div>

      {/* ── Keyframe animations (injected once) ───────────────────── */}
      <style>{`
        @keyframes selfie-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes selfie-pop-in {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
      `}</style>
    </>
  );
};

/** Shared button style for icon buttons in the header row */
const iconBtnStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  width: 34,
  height: 34,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background 0.15s",
  padding: 0,
};

const kbdStyle = {
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 4,
  padding: "1px 5px",
  fontSize: 10,
  fontFamily: "inherit",
  color: "rgba(255,255,255,0.5)",
};

export default SelfieModal;
